import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Button, Grid, CircularProgress, Alert, Tabs, Tab } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LookerStudioEmbed from '../components/dashboard/LookerStudioEmbed';
import ClientSelector from '../components/dashboard/ClientSelector';
import AddIcon from '@mui/icons-material/Add';

const LookerStudioDashboard = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const { clientId } = useParams();
  const navigate = useNavigate();

  // Carregar clientes ao montar o componente
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        const response = await axios.get('/api/clients', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'success') {
          setClients(response.data.data.clients);
          
          // Se temos um clientId na URL, usamos ele
          if (clientId) {
            const client = response.data.data.clients.find(c => c._id === clientId);
            if (client) {
              setSelectedClient(client);
            } else if (response.data.data.clients.length > 0) {
              // Se o cliente da URL não existe, use o primeiro
              setSelectedClient(response.data.data.clients[0]);
            }
          } else if (response.data.data.clients.length > 0) {
            // Se não há clientId na URL, use o primeiro cliente
            setSelectedClient(response.data.data.clients[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setLoading(false);
        
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      }
    };
    
    fetchClients();
  }, [clientId, navigate]);

  // Carregar embeds quando o cliente selecionado mudar
  useEffect(() => {
    const fetchEmbeds = async () => {
      if (!selectedClient) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await axios.get(`/api/clients/${selectedClient._id}/looker-embeds`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'success') {
          setEmbeds(response.data.data.embeds);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching Looker Studio embeds:', error);
        setError('Erro ao carregar dashboards. Por favor, tente novamente.');
        setLoading(false);
      }
    };
    
    fetchEmbeds();
  }, [selectedClient]);

  const handleClientChange = (client) => {
    setSelectedClient(client);
    setActiveTab(0); // Reset tab when changing client
    // Atualizar URL sem recarregar a página
    navigate(`/looker-studio/${client._id}`, { replace: true });
  };

  const handleRefreshDashboards = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddDashboard = () => {
    navigate(`/clients/${selectedClient._id}/looker-embeds`);
  };

  const handleManageDashboards = () => {
    navigate(`/clients/${selectedClient._id}/looker-embeds`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Client Selector e Ações */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <ClientSelector 
              clients={clients} 
              selectedClient={selectedClient} 
              onClientChange={handleClientChange} 
            />
            <Box sx={{ mt: { xs: 2, sm: 0 }, display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                onClick={handleRefreshDashboards} 
                disabled={loading || embeds.length === 0}
              >
                Atualizar Dashboards
              </Button>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleAddDashboard}
                disabled={!selectedClient}
              >
                Novo Dashboard
              </Button>
            </Box>
          </Paper>
        </Grid>

        {loading ? (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Grid>
        ) : !selectedClient ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h5">
                {clients.length > 0 
                  ? 'Selecione um cliente para visualizar os dashboards' 
                  : 'Nenhum cliente encontrado. Adicione seu primeiro cliente para começar.'}
              </Typography>
              {clients.length === 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button 
                    onClick={() => navigate('/clients/new')}
                    variant="contained"
                  >
                    Adicionar Cliente
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        ) : embeds.length === 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Nenhum dashboard configurado para este cliente
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Configure dashboards do Looker Studio para visualizar dados avançados de marketing.
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleAddDashboard}
              >
                Configurar Primeiro Dashboard
              </Button>
            </Paper>
          </Grid>
        ) : (
          <>
            {embeds.length > 1 && (
              <Grid item xs={12}>
                <Paper sx={{ mb: 2 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {embeds.map((embed, index) => (
                      <Tab key={embed._id} label={embed.name} />
                    ))}
                  </Tabs>
                </Paper>
              </Grid>
            )}
            
            <Grid item xs={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {embeds.length > 0 && activeTab < embeds.length && (
                <LookerStudioEmbed 
                  embed={embeds[activeTab]} 
                  refreshKey={refreshKey}
                />
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleManageDashboards}
                >
                  Gerenciar Dashboards
                </Button>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
};

export default LookerStudioDashboard;

